import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, X, Mail } from "lucide-react";
import { api } from "@/lib/api";

const STATUS = [
  { value: "new", label: "Nuovo", color: "#FF4D00" },
  { value: "in_progress", label: "In gestione", color: "#007A8A" },
  { value: "replied", label: "Risposto", color: "#52525b" },
  { value: "closed", label: "Chiuso", color: "#71717a" },
];

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/quotes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter((q) => q.status === filter);

  const update = async (id, patch) => {
    const { data } = await api.put(`/admin/quotes/${id}`, patch);
    setItems((prev) => prev.map((q) => (q.id === id ? data : q)));
    if (active?.id === id) setActive(data);
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la richiesta?")) return;
    await api.delete(`/admin/quotes/${id}`);
    toast.success("Eliminata.");
    setActive(null); load();
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-quotes">
      <div className="flex items-baseline justify-between mb-12 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-2">— PREVENTIVI</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Richieste</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-2 font-mono text-[10px] tracking-widest uppercase border ${filter === "all" ? "border-[#007A8A] text-[#007A8A]" : "border-[#e4e4e7] text-[#3f3f46]"}`}
          >Tutti ({items.length})</button>
          {STATUS.map((s) => (
            <button
              key={s.value}
              onClick={() => setFilter(s.value)}
              className={`px-3 py-2 font-mono text-[10px] tracking-widest uppercase border ${filter === s.value ? "border-[#007A8A] text-[#007A8A]" : "border-[#e4e4e7] text-[#3f3f46]"}`}
            >
              {s.label} ({items.filter((q) => q.status === s.value).length})
            </button>
          ))}
        </div>
      </div>

      <div className="border border-[#e4e4e7]">
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="border-b border-[#e4e4e7] font-mono text-[10px] tracking-widest uppercase text-[#3f3f46]">
              <th className="text-left p-4">Data</th>
              <th className="text-left p-4">Nome / Azienda</th>
              <th className="text-left p-4 hidden md:table-cell">Interesse</th>
              <th className="text-left p-4">Stato</th>
              <th className="text-right p-4">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((q) => {
              const st = STATUS.find((s) => s.value === q.status) ?? STATUS[0];
              return (
                <tr key={q.id} className="border-b border-[#e4e4e7] hover:bg-[#f4f4f5] cursor-pointer" onClick={() => setActive(q)} data-testid={`quote-row-${q.id.slice(0,8)}`}>
                  <td className="p-4 font-mono text-xs text-[#3f3f46]">
                    {new Date(q.created_at).toLocaleDateString("it-IT")}
                  </td>
                  <td className="p-4">
                    <div className="font-medium">{q.name}</div>
                    <div className="text-xs text-[#52525b]">{q.company || q.email}</div>
                  </td>
                  <td className="p-4 hidden md:table-cell text-[#3f3f46] text-xs">{q.interest}</td>
                  <td className="p-4">
                    <span className="font-mono text-[10px] tracking-widest" style={{ color: st.color }}>● {st.label.toUpperCase()}</span>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={(e) => { e.stopPropagation(); remove(q.id); }} className="p-2 hover:text-[#FF4D00]">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-[#52525b]">Nessuna richiesta.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-6" data-testid="admin-quote-detail">
          <div className="w-full max-w-3xl bg-[#f4f4f5] border border-[#e4e4e7] my-12">
            <div className="flex items-center justify-between p-6 border-b border-[#e4e4e7]">
              <div>
                <div className="font-mono text-[10px] tracking-widest text-[#007A8A] uppercase">
                  Richiesta · {new Date(active.created_at).toLocaleString("it-IT")}
                </div>
                <div className="font-display text-3xl tracking-tighter mt-1">{active.name}</div>
              </div>
              <button onClick={() => setActive(null)} aria-label="Chiudi"><X size={18} /></button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info label="Email"><a href={`mailto:${active.email}`} className="text-[#007A8A] aero-link">{active.email}</a></Info>
                <Info label="Telefono">{active.phone || "—"}</Info>
                <Info label="Azienda">{active.company || "—"}</Info>
                <Info label="Budget">{active.budget_range || "—"}</Info>
                <Info label="Interesse">{active.interest}</Info>
              </div>

              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#3f3f46] mb-2">Messaggio</div>
                <div className="border border-[#e4e4e7] p-4 bg-white whitespace-pre-line text-[#18181b]">{active.message}</div>
              </div>

              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#3f3f46] mb-2">Stato</div>
                <div className="flex gap-2 flex-wrap">
                  {STATUS.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => update(active.id, { status: s.value })}
                      className={`px-3 py-2 font-mono text-[10px] tracking-widest uppercase border ${active.status === s.value ? "border-[#007A8A] text-[#007A8A]" : "border-[#e4e4e7] text-[#3f3f46]"}`}
                    >{s.label}</button>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#3f3f46] mb-2">Note interne</div>
                <textarea
                  rows={3}
                  defaultValue={active.admin_notes ?? ""}
                  onBlur={(e) => update(active.id, { admin_notes: e.target.value })}
                  className="w-full bg-transparent border border-[#e4e4e7] focus:border-[#007A8A] p-3 text-sm font-body outline-none resize-none transition-colors"
                  placeholder="Solo per uso interno…"
                />
              </div>

              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#3f3f46] mb-2">Bozza risposta cliente</div>
                <textarea
                  rows={6}
                  defaultValue={active.reply ?? ""}
                  onBlur={(e) => update(active.id, { reply: e.target.value })}
                  className="w-full bg-transparent border border-[#e4e4e7] focus:border-[#007A8A] p-3 text-sm font-body outline-none resize-none transition-colors"
                  placeholder="Scrivi qui la proposta da inviare via email…"
                  data-testid="quote-reply-input"
                />
                <div className="mt-3 flex justify-end">
                  <a
                    href={`mailto:${active.email}?subject=${encodeURIComponent(`Preventivo Aéràs — ${active.interest}`)}&body=${encodeURIComponent(active.reply || "")}`}
                    className="btn-primary"
                    data-testid="quote-mailto"
                  >
                    <Mail size={14} /> Invia via email
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-widest uppercase text-[#52525b] mb-1">{label}</div>
      <div className="text-[#0a0a0a]">{children}</div>
    </div>
  );
}
